<template>
    <b-modal title-tag="h2" title="Merge Client" :lazy="true" :visible="isOpen">
        <b-alert variant="warning" show>
            Warning: The merge action you are requesting is IRREVERSIBLE.
            <br />
            Once a client is merged into another client, the merge cannot be
            undone. Do you want to merge these clients?
        </b-alert>

        <b-form-group label-cols="3" label="Client:" label-for="client">
            <b-form-input plaintext :value="name"></b-form-input>
        </b-form-group>

        <b-form-group label-cols="3" label="Merge Into:" label-for="merge">
            <v-select
                name="mergeinto"
                v-model="mergeIntoClient"
                placeholder="Client (required)"
                :options="clientList"
                @search="onSearch"
                v-validate="'required'"
                :class="{ 'border-danger': errors.has('mergeinto') }"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Reminders:" label-for="reminders">
            <b-select
                v-model="reminders"
                :options="[
                    {
                        text: 'Deactivate existing reminders',
                        value: '0'
                    },
                    {
                        text: 'Keep existing reminders',
                        value: '1'
                    }
                ]"
            />
        </b-form-group>

        <b-form-group label-cols="3" label="Notes:" label-for="notes">
            <b-form-textarea
                v-model="notes"
                placeholder="Notes (optional)"
                rows="5"
            />
        </b-form-group>

        <merge-confirmation
            ref="confirmationText"
            module="client"
            class="pt-1"
            :item-first="name"
            :item-second="mergeIntoClientName"
            @is-matched="isMatched => (isConfirmationTextMatch = isMatched)"
        />

        <template v-slot:modal-footer>
            <loader v-if="isLoading" :margin="1" />

            <b-button
                class="mr-auto"
                variant="secondary"
                @click="close"
                v-if="!isLoading"
            >
                Cancel
            </b-button>

            <b-button
                v-if="!isLoading"
                variant="primary"
                @click="proceed"
                :disabled="!enableMergeButton"
            >
                Continue to Merge
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import Client from "@/services/client";
import Loader from "@/module/commons/loader";
import { debounce } from "lodash";

export default {
    components: {
        Loader,
        vSelect: () => import("vue-select"),
        MergeConfirmation: () => import("@/module/commons/merge-confirmation")
    },
    data() {
        return {
            sourceId: null,
            name: "",
            isOpen: false,
            isLoading: false,
            mergeIntoClient: null,
            reminders: "0",
            notes: "",
            search: "",
            clientList: [],
            isConfirmationTextMatch: false
        };
    },
    computed: {
        mergeIntoClientName() {
            return this.mergeIntoClient ? this.mergeIntoClient.label : null;
        },
        enableMergeButton() {
            return this.isConfirmationTextMatch;
        }
    },
    methods: {
        open({ clientId, clientName }) {
            this.isOpen = true;
            this.sourceId = clientId;
            this.name = clientName;
        },
        onSearch(search, loading) {
            if (search.length && search.length > 2) {
                loading(true);
                this.searchClient(loading, search, this);
            }
        },
        searchClient: debounce((loading, search, vm) => {
            if (search) {
                let sourceId = vm.sourceId; // 'vm' now refers to the Vue instance

                Client.getClientList(search).then(response => {
                    let resource = response.resource;
                    resource = _.map(resource, element => {
                        if (element.index === 1) {
                            return null; // Skip element with index 1
                        }

                        return {
                            ...element,
                            label: `${element.fullName} (${element.index})`
                        };
                    }).filter(element => element !== null); // Remove null values
                    vm.clientList = _.filter(resource, function (obj) {
                        return obj.id != sourceId;
                    });

                    loading(false);
                });
            }
        }, 350),
        close() {
            this.isOpen = false;
        },
        async proceed() {
            const valid = await this.$validator.validateAll();
            const isConfirmationValid = this.$refs.confirmationText.isValid();

            if (!valid || !isConfirmationValid) {
                return false;
            }

            try {
                this.isLoading = true;

                const res = await Client.mergeClient({
                    source_actor_id: this.sourceId,
                    target_actor_id: this.mergeIntoClient.id,
                    reminder: this.reminders,
                    notes: this.notes
                });

                if (res.message && res.message.code === 200) {
                    this.isLoading = false;
                    this._message("Client merged successfully");
                    this.close();
                    this.$emit("onSuccess", this.mergeIntoClient.encoded_id);
                } else {
                    throw new Error("Error");
                }
            } catch (error) {
                this.isLoading = false;
                this._message("Error: Unable to merge client", "danger");
            }
        }
    }
};
</script>
